import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import { setFieldValue } from 'actions';
import { Autocomplete } from 'components/Autocomplete/Autocomplete';
import { MoneyInput } from 'components/FieldList/Field/MoneyInputField/MoneyInput';
import flywire from 'icons/flywire-header-logo.svg';
import type { RootState } from 'reducers/types';
import {
  getCurrentCountry,
  getCurrentCurrency,
  getGatewayUrl,
  getItems,
  getRecipientCountries,
  getWidgetMessage,
} from 'selectors';
import { useTranslations } from 'utils/translations/useTranslations';

type WidgetProps = ConnectedProps<typeof connector>;

const toOptions = (countries: { name: string; code: string }[]) =>
  countries.map(country => ({
    label: country.name,
    value: country.code,
  }));

const countryFieldName = 'sender_country';

const WidgetComponent = ({
  countries = [],
  country,
  currency,
  items,
  gatewayURL,
  setFieldValue,
  widgetMessage,
}: WidgetProps) => {
  const i18n = useTranslations();
  const hasOneItem = items.length === 1;
  const hasWidgetMessage = widgetMessage && widgetMessage.length > 0;
  const countryOptions = toOptions(countries);

  const handleCountryChange = (value: string) => {
    setFieldValue(countryFieldName, value);
  };

  return (
    <div className="padding-md">
      <img alt="flywire" className="marginBottom-lg" src={flywire} />
      {hasWidgetMessage && <p className="WidgetMessage">{widgetMessage}</p>}
      <Autocomplete
        name={countryFieldName}
        data-testid={countryFieldName}
        onChange={handleCountryChange}
        options={countryOptions}
        selected={country?.code}
      >
        <Autocomplete.Input
          data-testid={countryFieldName}
          placeholder={i18n.t('widget.country') as string}
        />
        <Autocomplete.Options />
      </Autocomplete>

      {hasOneItem && (
        <MoneyInput
          currencySymbol={currency?.symbol}
          decimalMark={currency?.decimalMark}
          label={items[0].label}
          maxLength={12}
          name={items[0].id}
          onChange={setFieldValue}
          subunitToUnit={currency?.subunitToUnit}
          symbolFirst={currency?.symbolFirst}
          thousandsSeparator={currency?.thousandsSeparator}
          value={items[0].defaultValue as number}
        />
      )}
      <p>{i18n.t('widget.continue.hint')}</p>
      <a
        data-testid="widgetContinue"
        href={gatewayURL}
        rel="noopener noreferrer"
        target="_blank"
        className="Button Button--primary"
      >
        {i18n.t('widget.continue.button')}
      </a>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  countries: getRecipientCountries(state),
  country: getCurrentCountry(state),
  currency: getCurrentCurrency(state),
  gatewayURL: getGatewayUrl(state),
  items: getItems(state),
  widgetMessage: getWidgetMessage(state),
});

const mapDispatchToProps = {
  setFieldValue,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const Widget = connector(WidgetComponent);

export { Widget };
